import axios, { AxiosError } from "axios";
import { useQuery } from "react-query";
import McmAnnouncement, {
  McmAnnouncementListWrapper,
  McmAnnouncementType,
} from "../model/McmAnnouncement";
import McmAnnouncementStats from "../model/McmAnnouncementStats";
import McmAnnouncementListRequest from "../request/McmAnnouncementListRequest";
import { HttpClientError } from "../../../../providers/HttpClient";

// --- Announcement List ---

export const McmAnnouncementListApiService = async (
  request: McmAnnouncementListRequest,
): Promise<McmAnnouncementListWrapper> => {
  try {
    const url = "/mcm-announcements/";
    const { data } = await axios.get(url, { params: request });
    return data;
  } catch (error) {
    const axiosError = error as AxiosError;
    const errorMessage = HttpClientError(axiosError);
    const customError = new Error(errorMessage);
    customError.cause = axiosError;
    throw customError;
  }
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useMcmAnnouncementListApiService = (
  request: McmAnnouncementListRequest,
) =>
  useQuery<
    McmAnnouncementListWrapper,
    Error | AxiosError
  >(["McmAnnouncementListApiService", request.page, request.page_size, request.status, request.ordering, request.search], () =>
    McmAnnouncementListApiService(request),
    { keepPreviousData: true },
  );

// --- Announcement Detail ---

export const McmAnnouncementDetailApiService = async (
  id: string,
): Promise<McmAnnouncement> => {
  try {
    const url = `/mcm-announcements/${id}/`;
    const { data } = await axios.get(url);
    return data;
  } catch (error) {
    const axiosError = error as AxiosError;
    const errorMessage = HttpClientError(axiosError);
    const customError = new Error(errorMessage);
    customError.cause = axiosError;
    throw customError;
  }
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useMcmAnnouncementDetailApiService = (
  id: string | null,
  enabled = true,
) =>
  useQuery<McmAnnouncement, Error | AxiosError>(
    ["McmAnnouncementDetailApiService", id],
    () => {
      if (!id) throw new Error("Announcement ID is required");
      return McmAnnouncementDetailApiService(id);
    },
    { enabled: enabled && !!id },
  );

// --- Announcement Stats ---

export const McmAnnouncementStatsApiService = async (
  id: string,
): Promise<McmAnnouncementStats> => {
  try {
    const url = `/mcm-announcements/${id}/stats/`;
    const { data } = await axios.get(url);
    return data;
  } catch (error) {
    const axiosError = error as AxiosError;
    const errorMessage = HttpClientError(axiosError);
    const customError = new Error(errorMessage);
    customError.cause = axiosError;
    throw customError;
  }
};

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useMcmAnnouncementStatsApiService = (
  id: string | null,
  enabled = true,
) =>
  useQuery<McmAnnouncementStats, Error | AxiosError>(
    ["McmAnnouncementStatsApiService", id],
    () => {
      if (!id) throw new Error("Announcement ID is required");
      return McmAnnouncementStatsApiService(id);
    },
    { enabled: enabled && !!id },
  );

// --- Announcement Type List ---

export const McmAnnouncementTypeListApiService =
  async (): Promise<McmAnnouncementType[]> => {
    try {
      const url = "/announcement_types/";
      const { data } = await axios.get(url);
      return data;
    } catch (error) {
      const axiosError = error as AxiosError;
      const errorMessage = HttpClientError(axiosError);
      const customError = new Error(errorMessage);
      customError.cause = axiosError;
      throw customError;
    }
  };

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const useMcmAnnouncementTypeListApiService = () =>
  useQuery<McmAnnouncementType[], Error | AxiosError>(
    ["McmAnnouncementTypeListApiService"],
    () => McmAnnouncementTypeListApiService(),
    { staleTime: 10 * 60 * 1000 },
  );
